import React, { memo, useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { InlineImageCache, RecordKind } from "../utils/recordFiles";
import {
  applyInlineImages,
  createInlineImageCache,
  loadInlineImages,
  revokeInlineImageCache,
} from "../utils/recordFiles";

/**
 * One ticket/CR tab's inline-image cache, revoked when the record changes, the modal closes (the
 * record id clears) or the tab unmounts — never when a preview opens and closes, because that only
 * unmounts and remounts the modal body and the remounted FeedBodys re-apply what is cached (ACOP-123).
 *
 * The revoke is a LAYOUT effect on purpose. React runs a parent's layout-effect cleanup before its
 * children's layout effects in the same commit, so on a record switch the old record's URLs are
 * revoked (and its in-flight loads orphaned) BEFORE the new record's FeedBodys start loading. A
 * passive effect would run after them and throw their loads away.
 */
export function useRecordFileCache(recordId: string | null | undefined): {
  imageCache: InlineImageCache;
  cacheVersion: number;
  onImagesSettled: () => void;
} {
  const cacheRef = useRef<InlineImageCache | null>(null);
  if (cacheRef.current === null) cacheRef.current = createInlineImageCache();
  const imageCache = cacheRef.current;
  const [cacheVersion, setCacheVersion] = useState(0);
  const onImagesSettled = useCallback(() => setCacheVersion((v) => v + 1), []);

  useLayoutEffect(() => () => revokeInlineImageCache(imageCache), [recordId, imageCache]);

  return { imageCache, cacheVersion, onImagesSettled };
}

type FeedBodyProps = {
  /** Already sanitized (sanitizeCommentBody). */
  html: string;
  imageCache: InlineImageCache;
  /** Bumped by the tab whenever an image load settles, so every body re-applies the cache. */
  cacheVersion: number;
  kind: RecordKind;
  recordId?: string | null;
  onImagesSettled?: () => void;
  /** Click-to-zoom: called with the loaded image's src and alt. */
  onImageClick?: (src: string, alt: string) => void;
};

/**
 * A feed message body (ACOP-123, UI-1).
 *
 * React 19 rewrites a dangerouslySetInnerHTML node whenever it gets a NEW {__html} object, even with
 * identical markup, which wipes any src set on an <img> after the fact. So the markup object is
 * memoised on the html string, and after every mount and every cache change the layout effect
 * re-applies what the tab already knows: a blob: src for a loaded image, a text placeholder for a
 * hidden or failed one. Only images the cache has never seen are requested.
 */
function FeedBody({ html, imageCache, cacheVersion, kind, recordId, onImagesSettled, onImageClick }: FeedBodyProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const markup = useMemo(() => ({ __html: html }), [html]);

  useLayoutEffect(() => {
    const missing = applyInlineImages(ref.current, imageCache);
    if (missing.length > 0 && recordId) {
      loadInlineImages(imageCache, missing, { kind, recordId, onSettled: onImagesSettled });
    }
  }, [html, cacheVersion, imageCache, kind, recordId, onImagesSettled]);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLSpanElement>) => {
      const target = e.target as HTMLElement;
      if (!onImageClick || target.tagName !== "IMG") return;
      e.preventDefault();
      e.stopPropagation();
      const src = (target as HTMLImageElement).currentSrc || target.getAttribute("src");
      if (!src) return;
      onImageClick(src, target.getAttribute("alt") || "image");
    },
    [onImageClick]
  );

  return <span ref={ref} onClick={handleClick} dangerouslySetInnerHTML={markup} />;
}

export default memo(FeedBody);
